export const CRA_REASSESSMENT_RESPONSE_PATH = '/cra-reassessment-response';

export const craReassessmentResearch = {
  canonicalUrl: `https://situsai.ca${CRA_REASSESSMENT_RESPONSE_PATH}`,
  title: 'CRA Reassessment File Checklist for Canadian CPAs | Orca',
  description:
    'Research / Internal Demo: Orca is testing a synthetic CRA reassessment workflow to learn what CPAs need from reviewable AI work.',
  stageLabel: 'Research / Internal Demo',
  headline: 'CRA reassessment file checklist for Canadian CPAs',
  coreClaim: 'We are testing a synthetic CRA reassessment workflow to learn what CPAs need from reviewable AI work.',
  researchCta: {
    label: 'Join the CPA workflow research',
    href: '/contact?interest=cpa-workflow-research',
  },
  discoveryCta: {
    label: 'Book a workflow discovery call',
    href: 'https://calendly.com/rcarloc029/30min',
  },
  boundaries: [
    'No private client data.',
    'Output is not client-ready or filing-ready.',
    'CPA review required.',
  ],
  researchComponents: [
    'Synthetic client profile and CRA reassessment fact pattern.',
    'Synthetic supporting documents and relevant Canadian public sources.',
    'Missing facts, reviewer questions, and scoring.',
  ],
  feedbackQuestions: [
    'Is the first-pass research output useful for CPA review?',
    'Are assumptions and missing facts clear?',
    'What would make the workflow unsafe or unusable?',
  ],
  checklist: [
    {
      title: 'Identify the notice and disputed issue',
      body: 'Record the notice date, tax year, account identifier, and the issue in dispute.',
    },
    {
      title: 'Organize facts and reasons by issue',
      body: 'Set out the relevant facts and reasons for each disputed issue.',
    },
    {
      title: 'Collect CRA correspondence and supporting documents',
      body: 'Keep the notice and related CRA correspondence with copies of documents that support the position.',
    },
    {
      title: 'Collect relevant business and tax records',
      body: 'Gather applicable returns and schedules, ledgers, invoices, receipts, contracts, and bank records.',
    },
    {
      title: 'Confirm representative details when applicable',
      body: 'Keep the authorized representative details with the working materials when a representative is involved.',
    },
    {
      title: 'Verify the filing record and deadline',
      body: 'Keep the filing confirmation or case number. Deadlines vary by taxpayer type and circumstances. Verify the current deadline from CRA guidance.',
    },
  ],
  workingFileStructure: [
    {
      title: 'Notice and deadline cover sheet',
      body: 'Keep the notice date, tax year, account identifier, disputed issue, and the deadline that was verified from current CRA guidance.',
    },
    {
      title: 'Issue register',
      body: 'List each disputed issue, the facts to confirm, the records to review, and the next review step.',
    },
    {
      title: 'Evidence grouped by issue',
      body: 'Group returns, schedules, ledgers, invoices, receipts, contracts, bank records, and correspondence under the related issue.',
    },
    {
      title: 'Correspondence and submission history',
      body: 'Keep CRA communications, call notes, submissions, and delivery details in date order.',
    },
    {
      title: 'Filing confirmation',
      body: 'Keep the filing confirmation or case number and record where the confirmation was verified.',
    },
  ],
  missingDocumentLog: {
    label: 'Missing document log',
    description: 'Track missing items by issue so the reviewer can see what remains unverified.',
    fields: ['Item', 'Related issue', 'Why needed', 'Owner/source', 'Requested date', 'Due date', 'Status', 'Received/verified'],
  },
  sources: [
    {
      title: 'CRA guide P148: objection and appeal rights under the Income Tax Act',
      url: 'https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/p148/p148-resolving-your-dispute-objection-appeal-rights-under-income-tax-act.html',
    },
    {
      title: 'Form T400A: Notice of Objection - Income Tax Act',
      url: 'https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/t400a.html',
    },
    {
      title: 'CRA: Keeping records',
      url: 'https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/keeping-records.html',
    },
    {
      title: 'CRA: Income tax objections decision tree',
      url: 'https://www.canada.ca/en/revenue-agency/services/about-canada-revenue-agency-cra/complaints-disputes/file-objection-cppei-appeal-minister/income-tax-decision-tree.html',
    },
  ],
  informationNotice:
    'This section summarizes general CRA procedural information. It does not describe an Orca product capability and is general information, not tax or legal advice.',
} as const;

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export function renderCraReassessmentResearchInitialContent(): string {
  const content = craReassessmentResearch;
  const boundaries = content.boundaries.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const researchComponents = content.researchComponents.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const feedbackQuestions = content.feedbackQuestions.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const checklist = content.checklist
    .map((item) => `<li><strong>${escapeHtml(item.title)}</strong><p>${escapeHtml(item.body)}</p></li>`)
    .join('');
  const workingFileStructure = content.workingFileStructure
    .map((item) => `<li><strong>${escapeHtml(item.title)}</strong><p>${escapeHtml(item.body)}</p></li>`)
    .join('');
  const missingDocumentFields = content.missingDocumentLog.fields
    .map((field) => `<li>${escapeHtml(field)}</li>`)
    .join('');
  const sources = content.sources
    .map((source) => `<li><a href="${escapeHtml(source.url)}">${escapeHtml(source.title)}</a></li>`)
    .join('');

  return `
  <main>
    <p>${escapeHtml(content.stageLabel)}</p>
    <h1>${escapeHtml(content.headline)}</h1>
    <p>${escapeHtml(content.coreClaim)}</p>
    <section>
      <h2>Research boundaries</h2>
      <ul>${boundaries}</ul>
    </section>
    <section>
      <h2>What we are testing</h2>
      <ul>${researchComponents}</ul>
    </section>
    <section>
      <h2>What we want CPA feedback on</h2>
      <ul>${feedbackQuestions}</ul>
    </section>
    <section>
      <h2>What should a Canadian CPA collect before reviewing a CRA reassessment?</h2>
      <p>CRA procedural information only. This checklist does not describe an Orca capability.</p>
      <ol>${checklist}</ol>
      <p>${escapeHtml(content.informationNotice)}</p>
      <h3>Official CRA sources</h3>
      <ul>${sources}</ul>
    </section>
    <section>
      <h2>Suggested working-file structure</h2>
      <p>Practical workflow guidance, not a CRA requirement.</p>
      <ol>${workingFileStructure}</ol>
      <h3>${escapeHtml(content.missingDocumentLog.label)}</h3>
      <p>${escapeHtml(content.missingDocumentLog.description)}</p>
      <ul>${missingDocumentFields}</ul>
    </section>
    <p><a href="${escapeHtml(content.researchCta.href)}">${escapeHtml(content.researchCta.label)}</a></p>
    <p><a href="${escapeHtml(content.discoveryCta.href)}">${escapeHtml(content.discoveryCta.label)}</a></p>
  </main>`;
}
